import { useEffect, useState } from 'react';
import { DefaultValues, FieldValues, FormProvider, Resolver, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ObjectSchema } from 'yup';

import { Button, CardActions, FormCancelButton, FormSubmitButton } from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { CancelRequestModal } from '../../../components/cancel-request-modal/cancel-request-modal.component';
import { EconomyCard } from './economy-card.component';

export interface EconomyEditableCardProps<TValues extends FieldValues> {
  title: string;
  defaultValues: DefaultValues<TValues>;
  schema: ObjectSchema<TValues>;
  /**
   * Persists the edit. **Omit it to render the card read-only** — no Rediger button, no form
   * submission, no success toast.
   *
   * A card whose save does not persist must not offer one: `handleValid` below reports success
   * unconditionally, so an `onSave` that discards its argument tells the user their edit was saved
   * when nothing left the browser, and the row reverts on the next refetch. Raised in review on
   * PR #625. The two chart cards use this until EXIO-2988 ships their PUT.
   */
  onSave?: (values: TValues) => void;
  /** Stack children as label/value rows, as on the read-only cards. */
  rowStack?: boolean;
  /**
   * Extra header controls shown only while editing, to the left of Avbryt/Lagre — where
   * the agreement cards put their "Legg til" button. Rendered inside the form, so it can
   * read the form context.
   */
  editActions?: React.ReactNode;
  children: (editing: boolean) => React.ReactNode;
}

/**
 * A section card that reads as text until you press Rediger, matching how the agreement
 * and portfolio cards behave: the card owns its own form, so saving one section never
 * validates or submits another.
 *
 * `children` is a render prop rather than a plain node so each field can pick its
 * read-only or editing form — the same `!editing ? … : …` split the agreement cards use.
 */
export function EconomyEditableCard<TValues extends FieldValues>({
  title,
  defaultValues,
  schema,
  onSave,
  rowStack,
  editActions,
  children,
}: EconomyEditableCardProps<TValues>) {
  const { t } = useTranslation('economy');
  const { t: tCommon } = useTranslation('common');

  const [editing, setEditing] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const methods = useForm<TValues>({
    defaultValues,
    mode: 'onTouched',
    // `yupResolver` reports its output as `MakeKeysOptional<TValues>`, which stops matching
    // `Resolver<TValues>` once `TValues` is a type parameter rather than a concrete shape.
    // The schema and `TValues` are the same type by construction at every call site.
    resolver: yupResolver(schema) as unknown as Resolver<TValues>,
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  // Leaving edit mode always restores the last saved values, so an abandoned edit does not
  // linger in the fields the next time the card is opened.
  useEffect(() => {
    if (!editing) reset(defaultValues);
  }, [defaultValues, editing, reset]);

  const handleValid = (values: TValues) => {
    // Unreachable while onSave is undefined -- the submit button is never rendered -- but guarded
    // rather than asserted, so a future caller cannot reach the success toast without persistence.
    if (!onSave) return;

    onSave(values);
    toast.success(t('settings.saved'));
    setEditing(false);
  };

  const handleInvalid = () => toast.error(t('settings.invalid'));

  const requestCancel = () => (isDirty ? setConfirmingCancel(true) : setEditing(false));

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={handleSubmit(handleValid, handleInvalid)}>
        <EconomyCard
          title={title}
          rowStack={rowStack}
          actions={
            <CardActions>
              {onSave == null ? null : !editing ? (
                <Button type="button" color="primary" size="sm" onClick={() => setEditing(true)}>
                  {tCommon('edit')}
                </Button>
              ) : (
                <>
                  {editActions}
                  <FormCancelButton type="button" color="primary-dark" size="sm" onClick={requestCancel}>
                    {tCommon('cancel')}
                  </FormCancelButton>
                  <FormSubmitButton type="submit" color="primary" size="sm" disableDirtyHandling>
                    {tCommon('save')}
                  </FormSubmitButton>
                </>
              )}
            </CardActions>
          }
        >
          {children(editing)}
        </EconomyCard>
      </form>

      <CancelRequestModal
        open={confirmingCancel}
        onCancel={() => setConfirmingCancel(false)}
        onConfirm={() => {
          setConfirmingCancel(false);
          setEditing(false);
        }}
      />
    </FormProvider>
  );
}

export default EconomyEditableCard;
